import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
  FlatList
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { collection, doc, getDoc, getDocs, query, updateDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { Exercise } from '../../types';
import { useTheme } from '../../context/ThemeContext';

type Props = NativeStackScreenProps<MainStackParamList, 'EditRoutine'>;

interface RoutineDay {
  order: number;
  name: string;
  exercises: RoutineExercise[];
}

interface RoutineExercise {
  exerciseId: string;
  exerciseName: string;
  series: number;
  repetitions: number;
  restTime: number; // in seconds
  muscleGroups?: string[];
}

const EditRoutineScreen = ({ route, navigation }: Props) => {
  const { theme } = useTheme();
  const { routineId } = route.params;
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState<RoutineDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState<RoutineDay | null>(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [dayName, setDayName] = useState('');
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [series, setSeries] = useState('');
  const [repetitions, setRepetitions] = useState('');
  const [restTime, setRestTime] = useState('');
  const [editingExerciseIndex, setEditingExerciseIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreator, setIsCreator] = useState(false);
  const [creatorName, setCreatorName] = useState('');
  const [viewMode, setViewMode] = useState<'edit' | 'view'>('view');

  useEffect(() => {
    fetchRoutineData();
    fetchExercises();
  }, []);

  const fetchRoutineData = async () => {
    try {
      setInitialLoading(true);
      
      const routineRef = doc(firestore, 'routines', routineId);
      const routineDoc = await getDoc(routineRef);
      
      if (!routineDoc.exists()) {
        Alert.alert('Error', 'No se encontró la rutina');
        navigation.goBack();
        return;
      }
      
      const routineData = routineDoc.data();
      const currentUserId = auth.currentUser?.uid;
      
      if (!currentUserId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Verificar si el usuario actual es el creador
      const isUserCreator = routineData.creatorId === currentUserId;
      setIsCreator(isUserCreator);
      setViewMode(isUserCreator ? 'edit' : 'view');
      
      // Si no es el creador, obtener el nombre del creador
      if (!isUserCreator && routineData.creatorId) {
        try {
          const creatorDoc = await getDoc(doc(firestore, 'users', routineData.creatorId));
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            setCreatorName(creatorData.displayName || 'Usuario');
          }
        } catch (error) {
          console.error('Error al obtener datos del creador:', error);
        }
      }
      
      // Actualizar el título de la pantalla según el modo
      navigation.setOptions({
        title: isUserCreator ? 'Editar Rutina' : 'Ver Rutina'
      });
      
      setName(routineData.name);
      setDescription(routineData.description || '');
      
      // Ordenar días por su orden
      if (routineData.days && Array.isArray(routineData.days)) {
        const sortedDays = [...routineData.days].sort((a, b) => a.order - b.order);
        setDays(sortedDays);
      }
    } catch (error) {
      console.error('Error al cargar la rutina:', error);
      Alert.alert('Error', 'No se pudo cargar la información de la rutina');
      navigation.goBack();
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchExercises = async () => {
    try {
      setExercisesLoading(true);
      const exercisesCollection = collection(firestore, 'exercises');
      const exercisesQuery = query(exercisesCollection);
      const querySnapshot = await getDocs(exercisesQuery);
      
      const exercisesList: Exercise[] = [];
      querySnapshot.forEach((doc) => {
        exercisesList.push({
          id: doc.id,
          ...doc.data()
        } as Exercise);
      });
      
      setExercises(exercisesList);
    } catch (error) {
      console.error('Error al obtener ejercicios:', error);
      Alert.alert('Error', 'No se pudieron cargar los ejercicios');
    } finally {
      setExercisesLoading(false);
    }
  };

  const addDay = () => {
    setError(null);
    if (!dayName.trim()) {
      setError('Por favor ingresa un nombre para el día');
      return;
    }
    
    const newDay: RoutineDay = {
      order: days.length + 1,
      name: dayName,
      exercises: []
    };
    
    setDays([...days, newDay]);
    setDayName('');
    setShowDayModal(false);
  };

  const editDay = (index: number) => {
    setCurrentDay(days[index]);
  };

  const removeDay = (index: number) => {
    Alert.alert(
      'Confirmar',
      '¿Estás seguro de eliminar este día?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Eliminar', 
          style: 'destructive',
          onPress: () => {
            const newDays = [...days];
            newDays.splice(index, 1);
            
            // Reordenar los días
            newDays.forEach((day, i) => {
              day.order = i + 1;
            });
            
            setDays(newDays);
          }
        }
      ]
    );
  };

  const addExerciseToDay = () => {
    setError(null);
    if (!currentDay) return;
    if (!selectedExercise) {
      setError('Por favor selecciona un ejercicio');
      return;
    }
    
    if (!series.trim() || !repetitions.trim()) {
      setError('Por favor completa las series y repeticiones');
      return;
    }
    
    const seriesValue = parseInt(series);
    const repetitionsValue = parseInt(repetitions);
    const restTimeValue = restTime.trim() ? parseInt(restTime) : 60;
    
    if (seriesValue <= 0 || repetitionsValue <= 0) {
      setError('Las series y repeticiones deben ser mayores a 0');
      return;
    }
    
    const newExercise: RoutineExercise = {
      exerciseId: selectedExercise.id,
      exerciseName: selectedExercise.name,
      series: seriesValue,
      repetitions: repetitionsValue,
      restTime: restTimeValue,
      muscleGroups: selectedExercise.muscleGroups
    };
    
    const dayIndex = days.findIndex(d => d.order === currentDay.order);
    if (dayIndex === -1) return;
    
    const newDays = [...days];
    
    if (editingExerciseIndex !== null) {
      // Editing existing exercise
      newDays[dayIndex].exercises[editingExerciseIndex] = newExercise;
    } else {
      // Adding new exercise
      newDays[dayIndex].exercises.push(newExercise);
    }
    
    setDays(newDays);
    setCurrentDay(newDays[dayIndex]);
    clearExerciseForm();
    setShowExerciseModal(false);
  };

  const prepareEditExercise = (exerciseIndex: number) => {
    if (!currentDay) return;
    
    const exercise = currentDay.exercises[exerciseIndex];
    const exerciseData = exercises.find(e => e.id === exercise.exerciseId);
    if (!exerciseData) return;
    
    setSelectedExercise({
      ...exerciseData,
      muscleGroups: exercise.muscleGroups || [],
    });
    
    setSeries(exercise.series.toString());
    setRepetitions(exercise.repetitions.toString());
    setRestTime(exercise.restTime.toString());
    setEditingExerciseIndex(exerciseIndex);
    setShowExerciseModal(true);
  };

  const removeExercise = (exerciseIndex: number) => {
    if (!currentDay) return;
    
    Alert.alert(
      'Confirmar',
      '¿Estás seguro de eliminar este ejercicio?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Eliminar', 
          style: 'destructive',
          onPress: () => {
            const dayIndex = days.findIndex(d => d.order === currentDay.order);
            if (dayIndex === -1) return;
            
            const newDays = [...days];
            newDays[dayIndex].exercises.splice(exerciseIndex, 1);
            
            setDays(newDays);
            setCurrentDay(newDays[dayIndex]);
          }
        }
      ]
    );
  };

  const clearExerciseForm = () => {
    setSelectedExercise(null);
    setSeries('');
    setRepetitions('');
    setRestTime('');
    setEditingExerciseIndex(null);
  };

  const updateRoutine = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Por favor ingresa un nombre para la rutina');
      return;
    }
    
    if (days.length === 0) {
      setError('La rutina debe tener al menos un día');
      return;
    }
    
    // Verificar que cada día tenga al menos un ejercicio
    for (const day of days) {
      if (day.exercises.length === 0) {
        setError(`El día ${day.name} no tiene ejercicios`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('No autenticado');
      }
      
      // Actualizar la rutina en Firestore
      const routineRef = doc(firestore, 'routines', routineId);
      
      await updateDoc(routineRef, {
        name,
        description,
        days,
        updatedAt: new Date(),
      });
      
      navigation.goBack();
    } catch (error) {
      console.error('Error al actualizar rutina:', error);
      setError('No se pudo actualizar la rutina');
    } finally {
      setLoading(false);
    }
  };

  const renderDayItem = ({ item, index }: { item: RoutineDay; index: number }) => (
    <Card key={`day-${item.order}`} style={styles.dayCard}>
      <View style={styles.dayHeader}>
        <Text style={[styles.dayTitle, { color: theme.colors.text }]}>Día {item.order}: {item.name}</Text>
        {isCreator && (
          <View style={styles.dayActions}>
            <TouchableOpacity
              style={styles.dayAction}
              onPress={() => {
                setCurrentDay(item);
                editDay(index);
              }}
            >
              <Ionicons name="create-outline" size={24} color={theme.colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dayAction}
              onPress={() => removeDay(index)}
            >
              <Ionicons name="trash-outline" size={24} color={theme.colors.error} />
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {item.exercises && item.exercises.length > 0 ? (
        <View style={styles.exercisesList}>
          {item.exercises.map((exercise, exIndex) => (
            <View key={`${exercise.exerciseId}-${exIndex}`} style={[styles.exerciseItem, { borderBottomColor: theme.colors.grey.light }]}>
              <View style={styles.exerciseInfo}>
                <Text style={[styles.exerciseName, { color: theme.colors.text }]}>{exercise.exerciseName}</Text>
                <Text style={[styles.exerciseDetails, { color: theme.colors.grey.dark }]}>
                  {exercise.series} series x {exercise.repetitions} repeticiones
                </Text>
                <Text style={[styles.exerciseRest, { color: theme.colors.grey.medium }]}>
                  Descanso: {exercise.restTime} segundos
                </Text>
              </View>
              {isCreator && (
                <View style={styles.exerciseActions}>
                  <TouchableOpacity
                    style={styles.exerciseAction}
                    onPress={() => prepareEditExercise(exIndex)}
                  >
                    <Ionicons name="create-outline" size={20} color={theme.colors.primary} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.exerciseAction}
                    onPress={() => removeExercise(exIndex)}
                  >
                    <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                  </TouchableOpacity>
                </View>
              )}
            </View>
          ))}
        </View>
      ) : (
        <View style={styles.emptyExercises}>
          <Text style={[styles.emptyExercisesText, { color: theme.colors.grey.dark }]}>No hay ejercicios</Text>
        </View>
      )}
      
      {isCreator && (
        <Button
          title="Agregar Ejercicio"
          onPress={() => {
            setCurrentDay(item);
            clearExerciseForm();
            setShowExerciseModal(true);
          }}
          type="outline"
          style={styles.addExerciseButton}
        />
      )}
    </Card>
  );

  const renderExerciseItem = ({ item }: { item: Exercise }) => (
    <TouchableOpacity
      style={[
        styles.exerciseSelectItem,
        { borderBottomColor: theme.colors.grey.light },
        selectedExercise?.id === item.id && [styles.exerciseSelectItemSelected, { backgroundColor: theme.colors.grey.light }]
      ]}
      onPress={() => setSelectedExercise(item)}
    >
      <View style={styles.exerciseSelectInfo}>
        <Text style={[styles.exerciseSelectName, { color: theme.colors.text }]}>{item.name}</Text>
        <View style={styles.exerciseSelectMuscles}>
          {item.muscleGroups.slice(0, 3).map((muscle, index) => (
            <Text key={index} style={[styles.exerciseSelectMuscle, { color: theme.colors.grey.dark }]}>
              {muscle}{index < Math.min(item.muscleGroups.length, 3) - 1 ? ', ' : ''}
            </Text>
          ))}
          {item.muscleGroups.length > 3 && (
            <Text style={[styles.exerciseSelectMuscle, { color: theme.colors.grey.dark }]}>...</Text>
          )}
        </View>
      </View>
      {selectedExercise?.id === item.id && (
        <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
      )}
    </TouchableOpacity>
  );

  if (initialLoading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView style={[styles.scrollView, { backgroundColor: theme.colors.background }]}>
        <View style={styles.container}>
          {!isCreator && (
            <Card style={styles.viewModeCard}>
              <View style={styles.viewModeContent}>
                <Ionicons name="lock-closed-outline" size={24} color={theme.colors.warning} />
                <View style={styles.viewModeTextContainer}>
                  <Text style={[styles.viewModeTitle, { color: theme.colors.text }]}>
                    Modo de solo lectura
                  </Text>
                  <Text style={[styles.viewModeDescription, { color: theme.colors.grey.dark }]}>
                    Esta rutina fue creada por {creatorName}. No puedes editarla, solo consultarla.
                  </Text>
                </View>
              </View>
            </Card>
          )}
          
          <Card style={styles.card}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Información Básica</Text>
            
            <Input
              label="Nombre de la Rutina"
              value={name}
              onChangeText={setName}
              placeholder="Ej. Rutina de Fuerza"
              darkMode={true}
              editable={isCreator}
            />
            
            <Input
              label="Descripción"
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
              placeholder="Describe el objetivo de esta rutina..."
              darkMode={true}
              editable={isCreator}
            />
          </Card>
          
          <View style={styles.daysHeader}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>Días de la Rutina</Text>
            {isCreator && (
              <TouchableOpacity
                style={styles.addDayButton}
                onPress={() => setShowDayModal(true)}
              >
                <Ionicons name="add-circle" size={24} color={theme.colors.primary} />
                <Text style={[styles.addDayText, { color: theme.colors.primary }]}>Agregar Día</Text>
              </TouchableOpacity>
            )}
          </View>
          
          {days.length > 0 ? (
            days.map((day, index) => renderDayItem({ item: day, index }))
          ) : (
            <Card style={styles.emptyDaysCard}>
              <Text style={[styles.emptyDaysText, { color: theme.colors.grey.dark }]}>
                No hay días en esta rutina. {isCreator ? 'Agrega un día para comenzar.' : ''}
              </Text>
            </Card>
          )}
          
          {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
          
          {isCreator && (
            <Button
              title="Guardar Cambios"
              onPress={updateRoutine}
              type="primary"
              loading={loading}
              style={styles.saveButton}
            />
          )}
        </View>
      </ScrollView>
      
      {/* Modales - Solo visibles en modo de edición */}
      {isCreator && (
        <>
          {/* Add Day Modal */}
          <Modal
            visible={showDayModal}
            animationType="slide"
            transparent={true}
            onRequestClose={() => setShowDayModal(false)}
          >
            <View style={styles.modalOverlay}>
              <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
                <View style={styles.modalHeader}>
                  <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Agregar Día</Text>
                  <TouchableOpacity onPress={() => setShowDayModal(false)}>
                    <Ionicons name="close" size={24} color={theme.colors.text} />
                  </TouchableOpacity>
                </View>
                
                <Input
                  label="Nombre del Día"
                  value={dayName}
                  onChangeText={setDayName}
                  placeholder="Ej. Pecho y Triceps"
                  darkMode={true}
                />
                
                {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
                
                <Button
                  title="Agregar"
                  onPress={addDay}
                  type="primary"
                  style={styles.modalButton}
                />
              </View>
            </View>
          </Modal>
          
          {/* Add/Edit Exercise Modal */}
          <Modal
            visible={showExerciseModal}
            animationType="slide"
            transparent={true}
            onRequestClose={() => {
              clearExerciseForm();
              setShowExerciseModal(false);
            }}
          >
            <View style={styles.modalOverlay}>
              <View style={[styles.exerciseModalContent, { backgroundColor: theme.colors.card }]}>
                <View style={styles.modalHeader}>
                  <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                    {editingExerciseIndex !== null ? 'Editar Ejercicio' : 'Agregar Ejercicio'}
                  </Text>
                  <TouchableOpacity 
                    onPress={() => {
                      clearExerciseForm();
                      setShowExerciseModal(false);
                    }}
                  >
                    <Ionicons name="close" size={24} color={theme.colors.text} />
                  </TouchableOpacity>
                </View>
                
                {selectedExercise ? (
                  <View style={[styles.selectedExerciseContainer, { borderBottomColor: theme.colors.grey.light }]}>
                    <Text style={[styles.selectedExerciseName, { color: theme.colors.primary }]}>{selectedExercise.name}</Text>
                    <TouchableOpacity
                      style={styles.changeExerciseButton}
                      onPress={() => setSelectedExercise(null)}
                    >
                      <Text style={[styles.changeExerciseText, { color: theme.colors.primary }]}>Cambiar</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <View>
                    <Text style={[styles.exerciseSelectTitle, { color: theme.colors.text }]}>Selecciona un Ejercicio</Text>
                    {exercisesLoading ? (
                      <ActivityIndicator size="large" color={theme.colors.primary} />
                    ) : (
                      <FlatList
                        data={exercises}
                        renderItem={renderExerciseItem}
                        keyExtractor={(item) => item.id}
                        style={styles.exerciseSelectList}
                      />
                    )}
                  </View>
                )}
                
                {selectedExercise && (
                  <View style={styles.exerciseFormContainer}>
                    <View style={styles.exerciseFormRow}>
                      <View style={styles.formHalf}>
                        <Input
                          label="Series"
                          value={series}
                          onChangeText={setSeries}
                          keyboardType="numeric"
                          placeholder="Ej. 3"
                          darkMode={true}
                        />
                      </View>
                      <View style={styles.formHalf}>
                        <Input
                          label="Repeticiones"
                          value={repetitions}
                          onChangeText={setRepetitions}
                          keyboardType="numeric"
                          placeholder="Ej. 12"
                          darkMode={true}
                        />
                      </View>
                    </View>
                    
                    <Input
                      label="Tiempo de Descanso (segundos)"
                      value={restTime}
                      onChangeText={setRestTime}
                      keyboardType="numeric"
                      placeholder="Ej. 60"
                      darkMode={true}
                    />
                    
                    {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
                    
                    <Button
                      title={editingExerciseIndex !== null ? "Guardar Cambios" : "Agregar"}
                      onPress={addExerciseToDay}
                      type="primary"
                      style={styles.modalButton}
                    />
                  </View>
                )}
              </View>
            </View>
          </Modal>
        </>
      )}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  container: {
    padding: spacing.lg,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  viewModeCard: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    backgroundColor: `${colors.warning}10`,
  },
  viewModeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewModeTextContainer: {
    flex: 1,
    marginLeft: spacing.md,
  },
  viewModeTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
    marginBottom: spacing.xs,
  },
  viewModeDescription: {
    fontSize: typography.fontSize.sm,
  },
  card: {
    marginBottom: spacing.lg,
    padding: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginBottom: spacing.md,
  },
  daysHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  addDayButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addDayText: {
    marginLeft: spacing.xs,
    fontWeight: '500',
  },
  dayCard: {
    marginBottom: spacing.md,
    padding: spacing.md,
  },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  dayTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  dayActions: {
    flexDirection: 'row',
  },
  dayAction: {
    marginLeft: spacing.sm,
  },
  emptyDaysCard: {
    marginBottom: spacing.lg,
    padding: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyDaysText: {
    textAlign: 'center',
  },
  exercisesList: {
    marginBottom: spacing.sm,
  },
  exerciseItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
  },
  exerciseInfo: {
    flex: 1,
  },
  exerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.xs,
  },
  exerciseDetails: {
    fontSize: typography.fontSize.sm,
  },
  exerciseRest: {
    fontSize: typography.fontSize.xs,
  },
  exerciseActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  exerciseAction: {
    marginLeft: spacing.sm,
  },
  emptyExercises: {
    padding: spacing.md,
    alignItems: 'center',
  },
  emptyExercisesText: {
  },
  addExerciseButton: {
    marginTop: spacing.sm,
  },
  saveButton: {
    marginBottom: spacing.xl,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 500,
  },
  exerciseModalContent: {
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  modalButton: {
    marginTop: spacing.md,
  },
  exerciseSelectTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.sm,
  },
  exerciseSelectList: {
    maxHeight: 200,
  },
  exerciseSelectItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: 1,
  },
  exerciseSelectItemSelected: {
  },
  exerciseSelectInfo: {
    flex: 1,
  },
  exerciseSelectName: {
    fontSize: typography.fontSize.md,
  },
  exerciseSelectMuscles: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  exerciseSelectMuscle: {
    fontSize: typography.fontSize.xs,
  },
  selectedExerciseContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
    borderBottomWidth: 1,
  },
  selectedExerciseName: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  changeExerciseButton: {
    padding: spacing.xs,
  },
  changeExerciseText: {
    fontSize: typography.fontSize.sm,
  },
  exerciseFormContainer: {
    marginTop: spacing.sm,
  },
  exerciseFormRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  formHalf: {
    width: '48%',
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    marginVertical: spacing.sm,
    textAlign: 'center',
  },
});

export default EditRoutineScreen; 